// "Atanan Çağrılar" — the daily auto-assigned calls (owner 2026-10-02, Task Board #163; weekly
// plan #171, owner 2026-10-05; daily mix per call agent since #281, owner 2026-10-26).
//
// Every morning the bot (src/jobs/assign-calls.js) hands the QMS specialists yesterday's calls:
// for each call agent who worked, the brand's daily mix (Vegas 20 = 10 negative + 6 Olumlu + 3
// Başkası Açtı + 1 Meşgul; Prime 17 = 9 + 5 + 2 + 1). The plan and the week's progress come from
// the server (bot.plans, week) — nothing here hardcodes a number. Each row is
// a pre-filled evaluation: "Değerlendir" opens the form with agent, user id, call time and list
// already in place (#/new/<id>); the specialist adds the recording and scores it, and saving
// closes the row. A call that cannot be evaluated (no recording, wrong call) is skipped with a
// reason by a SUPERVISOR/ADMIN, and can be reopened — a QMS specialist has no skip (owner
// 2026-10-04, Task Board #169/#170): the server 403s it and `canSkip` hides the buttons.
//
// A qms sees only their own rows (server); a supervisor/admin sees every specialist's, grouped
// by specialist, and an admin can hand out today's calls immediately.
//
// Precise call tracking (migration 019): a call placed from the 2.1.0 softphone carries its own
// record — "Çağrı" is then the DIAL time (not when the outcome was logged), "Konuşma" the
// answered pill + talk time (≈ = estimated). An open row the recording fetcher tried and could
// not stage says "Kayıt yok: <reason>", and the bot's last run (assign_runs) lists what it
// handed out per specialist/brand and the call-agent names it could not match.

import { useState } from 'react';
import * as api from '../api';
import { ApiError } from '../api';
import type { Assignment, AssignmentLastRun, AssignmentOutcome, AssignmentRunResult, Meta, PanelUser } from '../types';
import { useAsync } from '../hooks';
import { Avatar, Callout, Empty, Icon, Loading, Note, PageHead, Panel, Pill } from '../ui';
import { shortDate, shortDateTime } from '../format';

export const OUTCOME_LABEL: Record<AssignmentOutcome, string> = {
  negative: 'Olumsuz',
  positive: 'Olumlu',
  answered_by_other: 'Başkası Açtı',
  busy: 'Meşgul',
  hung_up: 'Kapattı',
};
const OUTCOME_TONE: Record<AssignmentOutcome, 'good' | 'bad' | 'warn' | 'neu'> = {
  negative: 'bad',
  positive: 'good',
  answered_by_other: 'warn',
  busy: 'neu',
  hung_up: 'warn',
};

/** The row's label: the negative bucket names which of its three sheet statuses it is. */
export function outcomeLabel(r: Pick<Assignment, 'outcome' | 'sourceStatus'>): string {
  if (r.outcome === 'negative' && r.sourceStatus === 'do_not_call') return 'Aranmak İstemiyor';
  if (r.outcome === 'negative' && r.sourceStatus === 'hung_up_mid_call') return 'Kapattı';
  return OUTCOME_LABEL[r.outcome] ?? r.outcome;
}

/** Talk time from the softphone (migration 019): 192 → '3:12', 3725 → '1:02:05'. */
export function talkTime(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// The recording fetcher's miss reasons (POST /api/integrations/qms-assignment-recording-miss) →
// Turkish. An unknown code prints as-is: a new reason from a newer fetcher stays readable.
const REC_MISS_LABEL: Record<string, string> = {
  no_match: 'eşleşen çağrı bulunamadı',
  no_recording: 'çağrının kaydı yok',
  not_answered: 'çağrı cevaplanmamış',
  incoming: 'gelen çağrı',
  not_agents: 'başka temsilcinin çağrısı',
  started_after: 'çağrı sonuçtan sonra başlamış',
  still_running: 'çağrı hâlâ sürüyordu',
  too_old: 'çağrı çok eski',
  ext_mismatch: 'dahili uyuşmuyor',
};

/** "Kayıt yok: <reason>" for an open row the fetcher tried and could not stage. */
export function recMissText(r: Pick<Assignment, 'recLastMiss'>): string {
  return `Kayıt yok: ${REC_MISS_LABEL[r.recLastMiss] ?? r.recLastMiss}`;
}

/** The softphone's answered flag as a pill; nothing when unknown (older extension build). */
export function AnsweredPill({ answered }: { answered: boolean | null | undefined }) {
  if (answered === true) return <Pill tone="good">Cevaplandı</Pill>;
  if (answered === false) return <Pill tone="bad">Cevapsız</Pill>;
  return null;
}

export default function Assignments({ user, meta, brand }: { user: PanelUser; meta: Meta | null; brand: string }) {
  // '' = today (Istanbul), resolved by the server.
  const [day, setDay] = useState('');
  const state = useAsync(() => api.listAssignments(day || undefined, brand), [day, brand]);
  const data = state.data;
  // A specialist's own worklist. A qms with the read grant (viewAll, migration 022) sees every
  // specialist's rows, grouped like a supervisor's, and may evaluate any of them (`canWork`).
  const isQms = user.role === 'qms' && user.viewAll !== true;

  const [skipping, setSkipping] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [run, setRun] = useState<AssignmentRunResult | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await fn();
      setSkipping(null);
      setReason('');
      state.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'İşlem başarısız.');
    } finally {
      setBusy(false);
    }
  };

  const runNow = () => act(async () => { setRun(await api.runAssignments()); });

  // Group by specialist (a qms has one group: their own).
  const groups = new Map<number, { name: string; rows: Assignment[] }>();
  for (const r of data?.rows ?? []) {
    if (!groups.has(r.assigneeId)) groups.set(r.assigneeId, { name: r.assigneeName, rows: [] });
    groups.get(r.assigneeId)!.rows.push(r);
  }

  const brandName = (key: string) => meta?.brands.find((b) => b.key === key)?.name ?? key;
  const plans = (data?.bot.plans ?? []).filter((p) => !brand || p.brandKey === brand);
  const weekRows = (data?.week.rows ?? []).filter((w) => !brand || w.brandKey === brand);

  return (
    <>
      <PageHead
        icon="assignment"
        title="Atanan Çağrılar"
        subtitle={isQms
          ? 'Bot her sabah dün çalışan her temsilcinin günlük kotasını dünkü çağrılarından sana atar. Değerlendir ile form hazır açılır; kaydı ekleyip puanla.'
          : 'Botun her sabah dün çalışan her temsilcinin günlük kotasını QMS uzmanlarına atadığı çağrılar.'}
        right={data ? (
          <div className="row" style={{ gap: 8 }}>
            {data.canRun ? (
              <button className="btn soft sm" type="button" disabled={busy || !data.bot.enabled} onClick={runNow}>
                <Icon name="play_arrow" /><span>Şimdi dağıt</span>
              </button>
            ) : null}
            <select className="inp auto" value={data.day} onChange={(ev) => setDay(ev.target.value)} aria-label="Gün">
              {data.days.map((d) => (
                <option key={d} value={d}>{d === data.today ? `Bugün · ${shortDate(`${d}T12:00:00Z`)}` : shortDate(`${d}T12:00:00Z`)}</option>
              ))}
            </select>
          </div>
        ) : null}
      />

      {data && !data.bot.enabled ? (
        <Callout tone="warn">Otomatik atama kapalı — entegrasyon anahtarı ayarlı değil.</Callout>
      ) : null}
      {data && data.bot.enabled ? (
        <Callout tone="neu" icon="schedule">
          <div>{`Her gün ${data.bot.runAt} sonrası, dünkü çağrılardan · dün çalışan her temsilciye günlük kota`}</div>
          {plans.map((p) => (
            <div key={p.brandKey}>
              {`${brandName(p.brandKey)}: temsilci başına günde ${p.daily} — ${p.mix.negative} olumsuz (olumsuz / aranmak istemiyor / kapattı) · ${p.mix.positive} olumlu · ${p.mix.answered_by_other} başkası açtı · ${p.mix.busy} meşgul · haftada ${p.weeklyPerAgent}`}
            </div>
          ))}
          <div>Başkası açtı yetmezse meşgulden; bir tür eksik kalırsa önce olumludan tamamlanır.</div>
        </Callout>
      ) : null}
      {data && weekRows.length > 0 ? (
        <Callout tone="neu" icon="calendar_today">
          {weekRows.map((w) => (
            <div key={`${w.assigneeId}-${w.brandKey}`}>
              {`Bu hafta · ${w.assigneeName} · ${brandName(w.brandKey)}: ${w.assigned}${w.weekly != null ? `/${w.weekly}` : ''} atandı · ${w.done} değerlendirildi`}
            </div>
          ))}
        </Callout>
      ) : null}
      {data && data.counts.total > 0 ? (
        <Callout tone={data.counts.open === 0 ? 'good' : 'neu'}>
          {`${data.counts.done} değerlendirildi · ${data.counts.open} bekliyor · ${data.counts.skipped} atlandı`}
        </Callout>
      ) : null}

      {data?.bot.lastRun ? <LastRun lastRun={data.bot.lastRun} brandName={brandName} /> : null}
      {run ? <RunSummary run={run} /> : null}
      {error ? <Note error>{error}</Note> : null}
      {state.error ? <Note error>{state.error}</Note> : null}
      {state.loading && !data ? <Loading /> : null}
      {data && data.rows.length === 0 ? (
        <Empty>{data.day === data.today ? 'Bugün için atanmış çağrı yok.' : 'Bu gün için atanmış çağrı yok.'}</Empty>
      ) : null}

      {[...groups.entries()].map(([assigneeId, g]) => (
        <Panel
          key={assigneeId}
          icon={isQms ? 'assignment' : 'person'}
          title={isQms ? 'Çağrılarım' : g.name}
          hint={`${g.rows.filter((r) => r.status === 'done').length}/${g.rows.length} değerlendirildi`}
        >
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Sonuç</th><th>Agent</th><th>Kullanıcı ID</th><th>Çağrı</th><th>Konuşma</th><th>Liste</th><th>Durum</th><th />
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r) => (
                  <AssignmentRow
                    key={r.id}
                    r={r}
                    busy={busy}
                    canSkip={data?.canSkip === true}
                    skipping={skipping === r.id}
                    reason={reason}
                    onReason={setReason}
                    onSkipOpen={() => { setSkipping(r.id); setReason(''); setError(''); }}
                    onSkipCancel={() => setSkipping(null)}
                    onSkip={() => act(() => api.skipAssignment(r.id, reason))}
                    onReopen={() => act(() => api.reopenAssignment(r.id))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}
    </>
  );
}

function AssignmentRow({ r, busy, canSkip, skipping, reason, onReason, onSkipOpen, onSkipCancel, onSkip, onReopen }: {
  r: Assignment; busy: boolean; canSkip: boolean; skipping: boolean; reason: string;
  onReason: (s: string) => void; onSkipOpen: () => void; onSkipCancel: () => void;
  onSkip: () => void; onReopen: () => void;
}) {
  const status = r.status === 'done'
    ? <Pill tone="good">Değerlendirildi</Pill>
    : r.status === 'skipped' ? <Pill tone="neu">Atlandı</Pill> : <Pill tone="warn">Bekliyor</Pill>;
  return (
    <>
      <tr>
        <td><Pill tone={OUTCOME_TONE[r.outcome] ?? 'neu'}>{outcomeLabel(r)}</Pill></td>
        <td>
          <div className="who">
            <Avatar name={r.agentName} kind="neutral" />
            <span className="nm">{r.agentName}</span>
          </div>
        </td>
        <td className="tnum" translate="no">{r.playerUserId}</td>
        <td className="tnum">{shortDateTime(r.callAt)}</td>
        <td>
          {/* The softphone's record of the call (migration 019): answered + talk time. A call
              without a session (older extension build) shows a dash. */}
          {r.answered === null && r.talkSec === null ? '—' : (
            <div className="row" style={{ gap: 6 }}>
              <AnsweredPill answered={r.answered} />
              {r.talkSec !== null ? (
                <span className="tnum" translate="no" title={r.talkSource === 'estimated' ? 'tahmini' : undefined}>
                  {r.talkSource === 'estimated' ? `≈${talkTime(r.talkSec)}` : talkTime(r.talkSec)}
                </span>
              ) : null}
            </div>
          )}
        </td>
        <td>{r.listLabel ? <Pill tone="neu">{r.listLabel}</Pill> : '—'}</td>
        <td>
          <div className="row" style={{ gap: 6 }}>
            {status}
            {/* The bot already fetched the recording for this call (Task Board #163). */}
            {r.audioCount > 0 && r.status !== 'done' ? (
              <span className="row" style={{ gap: 2 }} title="Kayıt hazır">
                <Icon name="graphic_eq" style={{ color: 'var(--accent)', fontSize: 16 }} />
              </span>
            ) : null}
          </div>
          {r.status === 'skipped' && r.skipReason ? <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>{r.skipReason}</div> : null}
          {/* The fetcher tried and staged nothing — say why instead of leaving a bare row. */}
          {r.status === 'open' && r.audioCount === 0 && r.recLastMiss ? (
            <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>{recMissText(r)}</div>
          ) : null}
        </td>
        <td className="r">
          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
            {r.status === 'open' ? (
              <>
                {r.canWork !== false ? (
                  <a className="btn pri sm" href={`#/new/${r.id}`}><Icon name="playlist_add_check" /><span>Değerlendir</span></a>
                ) : null}
                {canSkip && !skipping ? (
                  <button className="btn ghost sm" type="button" disabled={busy} onClick={onSkipOpen}><span>Atla</span></button>
                ) : null}
              </>
            ) : null}
            {r.status === 'done' && r.evaluationId ? (
              <a className="btn soft sm" href={`#/evaluation/${r.evaluationId}`}>İncele</a>
            ) : null}
            {canSkip && r.status === 'skipped' ? (
              <button className="btn ghost sm" type="button" disabled={busy} onClick={onReopen}><span>Geri al</span></button>
            ) : null}
          </div>
        </td>
      </tr>
      {canSkip && skipping ? (
        <tr>
          <td colSpan={8}>
            <div className="row" style={{ gap: 8 }}>
              <input
                className="inp" style={{ flex: 1 }} value={reason} maxLength={500}
                onChange={(ev) => onReason(ev.target.value)}
                placeholder="Neden atlanıyor? (ör. kayıt bulunamadı, yanlış çağrı)"
              />
              <button className="btn pri sm" type="button" disabled={busy || !reason.trim()} onClick={onSkip}><span>Atla</span></button>
              <button className="btn ghost sm" type="button" disabled={busy} onClick={onSkipCancel}><span>Vazgeç</span></button>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** The bot's last run in scope (assign_runs): per specialist + brand, and the names it could not match. */
function LastRun({ lastRun, brandName }: { lastRun: AssignmentLastRun; brandName: (key: string) => string }) {
  const unmatched = [...new Set(lastRun.rows.flatMap((r) => r.unmatched))].sort((a, b) => a.localeCompare(b, 'tr'));
  const failed = lastRun.rows.some((r) => r.errors.length > 0);
  return (
    <Callout tone={failed ? 'warn' : 'neu'} icon="history">
      <div>{`Botun son çalışması: ${shortDateTime(lastRun.at)}`}</div>
      {lastRun.rows.map((r) => (
        <div key={`${r.assigneeId}-${r.brandKey}`}>
          {r.errors.length
            ? `${r.assigneeName} · ${r.brandKey ? brandName(r.brandKey) : '—'}: kaynak okunamadı`
            : `${r.assigneeName} · ${r.brandKey ? brandName(r.brandKey) : '—'}: ${r.assigned} atandı${r.short > 0 ? ` · ${r.short} eksik` : ''}`}
        </div>
      ))}
      {unmatched.length ? <div>{`Eşleşmeyen isim (${unmatched.length}): ${unmatched.join(', ')}`}</div> : null}
    </Callout>
  );
}

function RunSummary({ run }: { run: AssignmentRunResult }) {
  if (!run.ran) return <Callout tone="warn">Atama çalışmadı.</Callout>;
  if (!run.results.length) return <Callout tone="neu">Bugün herkese zaten çağrı atanmış.</Callout>;
  return (
    <Callout tone="good" icon="play_arrow">
      {run.results.map((r) => (
        <div key={r.userId}>
          {r.errors.length
            ? `${r.name}: kaynak okunamadı, sonra tekrar denenecek`
            : `${r.name}: ${r.assigned} çağrı atandı`
              + (r.short > 0 ? ' (eksik kaldı)' : '')
              + (r.unmatched.length ? ` · eşleşmeyen isim: ${r.unmatched.length}` : '')}
        </div>
      ))}
    </Callout>
  );
}
